'use strict';
const find = require('lodash/find');
const angular = require('angular');
const {copy, equals, forEach, isNumber, isUndefined} = angular;
var template = require('./builder.html');

module.exports.directive = [
  FormBuilderDirective];

module.exports.controller = [
  '$scope',
  '$element',
  '$attrs',
  '$timeout',
  'FormUtils',
  'FormDomain',
  '$ionicListDelegate',
  'FORM_SAVE_RESULT',
  FormBuilderController];

/**
 * Attributes
 *   create-form (not observed yet) if true, instruct the directive to create a new form.
 *
 * Scope
 *   Public
 *     model (FormDomain) The form instance to edit.
 *     api (Object) Pass in an empty object and it will be populated with the components public API.
 *      -Methods
 *       addField Adds a new field to the end of the list with default configuration
 *       removeField Removes the field from the current edit list and queues it to be deleted if changes are saved
 *       save Saves the form and all fields
 *       cancel Reverts the form and all fields to their current values from the server
 *
 *   Private
 *    -data
 *     fields (Array) The list of fields currently being edited
 *
 *    -Methods
 *     onDropComplete reorders the field array and updates the affected fields' ordinals.
 *
 *
 */
function FormBuilderDirective() {
  return {
    restrict: 'E',
    template: template,
    scope: {
      model: '=',
      api: '=?builderApi'
    },
    controllerAs: 'formBuilder',
    bindToController: true,
    controller: 'FormBuilderController'
  };
}

// TODO: either trim view mode A LOT or in sorting mode, trim fields down to just a type: name list item.
function FormBuilderController($scope, $element, $attrs, $timeout, FormUtils, FormDomain, $ionicListDelegate, FORM_SAVE_RESULT) {
  var vm = this;
  var currentModelId;
  var originalFields;
  var newForm = false;
  var ionList;
  vm.api = vm.api || {};
  vm.fieldApis = {};
  vm.onDropComplete = onDropComplete;
  vm.toggleSort = toggleSort;

  vm.canSort = false;

  $timeout(function () {
    ionList = $ionicListDelegate.$getByHandle('form-builder');
  }, 50);

  // Define API
  Object.defineProperties(vm.api, {
    createForm: {enumerable: true, value: createForm},
    getForm: {enumerable: true, value: getForm},
    setForm: {enumerable: true, value: setForm},
    addField: {enumerable: true, value: addField},
    removeField: {enumerable: true, value: removeField},
    isFieldPersisted: {enumerable: true, value: isFieldPersisted},
    isDirty: {enumerable: true, value: isDirty},
    isEmptyModel: {enumerable: true, value: isEmptyModel},
    validate: {enumerable: true, value: validateForm},
    save: {enumerable: true, value: save},
    cancel: {enumerable: true, value: cancel}
  });

  initBuilder();
  initModeToggleListener();

  function initBuilder() {
    if ($attrs.createForm === 'true')
      createForm();

    $scope.$watch(function () {
      return vm.model && vm.model.$id;
    }, function (modelId) {
      if (modelId && (modelId !== currentModelId)) {
        currentModelId = modelId;
        getFields();
      } else if (!modelId) {
        vm.fields = [];
      }
    });
  }

  function getFields() {
    var modelId = vm.model.$id;
    vm.model.getCurrentVersion().then(function (version) {
      if (currentModelId !== modelId) return;

      if (version) {
        originalFields = version.fields;
        vm.fields = copy(version.fields);
      } else {
        vm.fields = originalFields = [];
      }

      $timeout(function () {
        forEach(vm.fields, function (field) {
          setFieldMode(field, 'builder-view');
        });
      }, 50);
    });
  }

  function createForm() {
    newForm = true;
    vm.model = FormDomain.$factory.create();
    currentModelId = vm.model.$id; // block watcher from resetting the field data.
    vm.fields = [];
    addField();
  }

  function getForm() {
    return vm.model;
  }

  function setForm(formData) {
    if (vm.model && (!formData || vm.model.$id !== formData.$id)) {
      vm.model.$revert();
    }

    if (!formData) {
      newForm = false;
      vm.model = null;
      vm.fields = null;
    } else if (!vm.model || (vm.model.$id !== formData.$id)) {
      newForm = !formData.$isPersisted();
      vm.model = formData;
      currentModelId = vm.model.$id;
      getFields();
    }
  }

  function addField() {
    toggleFieldModes();
    vm.fields.push(FormUtils.generateField());
  }

  function removeField(field) {
    removeFromFields(vm.fields.indexOf(field));
  }

  function isFieldPersisted(field) {
    if (!vm.model.$isPersisted()) {
      return false;
    } else {
      return !!find(originalFields, function (it) {
        return it.id === field.id;
      })
    }
  }


  /*****************************************************************************************************************
   * Database methods
   ****************************************************************************************************************/

  function save() {
    if (!validateForm()) {
      // need to inform user
      //console.log('errors, canceling save');
      return Promise.resolve(FORM_SAVE_RESULT.VALIDATION_FAILED);
    }

    if (newForm) {
      return FormDomain.createForm(vm.model, vm.fields).then(setForm).then(resolveSuccess).catch(logError);
    } else if (vm.model.$isDirty() || areFieldsDirty()) {
      return FormDomain.updateForm(vm.model, vm.fields).then(updateOriginalFields).then(resolveSuccess).catch(logError);
    } else {
      return Promise.resolve(FORM_SAVE_RESULT.NO_CHANGES);
    }
  }

  function resolveSuccess() {
    return FORM_SAVE_RESULT.SAVED;
  }

  function updateOriginalFields() {
    originalFields = copy(vm.fields);
  }

  function logError(error) {
    console.error(error);
    return Promise.reject(error);
  }

  // reset the local field data array to the database version
  // revert changes to all persisted fields
  function cancel() {
    vm.model.$revert();
    getFields();
  }

  // TODO: need new validation impl as fields are no longer domain instances
  function validateForm() {
    var formIsValid = true;

    vm.model.$validate();

    if (vm.model.$hasErrors()) {
      //console.log('form errors', vm.model.$errors);
      formIsValid = false;
    }

    // TODO: convert to json-schema validator
    var i = vm.fields.length;
    while (i--) {
      var validation = FormUtils.validateField(vm.fields[i]);
      if (validation !== true) {
        console.warn('field ' + vm.fields[i].title + ' errors', validation);
        formIsValid = false;
      }
    }

    return formIsValid;
  }

  function isDirty() {
    return (vm.model && vm.model.$isDirty()) || areFieldsDirty();
  }

  function areFieldsDirty() {
    if (!vm.fields) {
      return false;
    } else {
      if (!originalFields || originalFields.length !== vm.fields.length) {
        return true;
      } else {
        for (var i = 0, length = originalFields.length; i < length; i++) {
          if (!equals(originalFields[i], vm.fields[i]))
            return true;
        }
        return false;
      }
    }
  }

  /**
   * Returns true if the model is not set or if the model is an untouched new form.
   * @returns {boolean}
   */
  function isEmptyModel() {
    if (!vm.model || vm.model.$isPersisted() || !vm.fields) {
      return false;
    } else if (vm.fields.length !== 1) {
      return false;
    } else if (!equals(vm.fields[0], FormUtils.generateField())) {
      return false;
    } else {
      return vm.model.isEmpty();
    }
  }

  function toggleSort() {
    if (!ionList) return;
    vm.canSort = !vm.canSort;
    ionList.showReorder(vm.canSort);
  }


  /*****************************************************************************************************************
   * view model fields list methods
   ****************************************************************************************************************/

  function addToFields(field, atIndex) {
    if (isNumber(atIndex) && atIndex > -1) {
      if (isUndefined(vm.fields[atIndex])) {
        vm.fields[atIndex] = field;
      } else {
        vm.fields.splice(atIndex, 0, field);
      }
    } else {
      vm.fields.push(field);
    }
  }

  function removeFromFields(index) {
    vm.fields.splice(index, 1);
  }


  /*****************************************************************************************************************
   * drag and drop
   ****************************************************************************************************************/

  // TODO: Test this out. we'll probably just replace this with ionic's ion-item's built in DnD support
  function onDropComplete(draggedIndex, droppedIndex, draggedField) {//, $event) {
    droppedIndex = __calcEffectiveDropIndex(droppedIndex);
    var draggedIndex = vm.fields.indexOf(draggedField);
    if (droppedIndex === draggedIndex) return;

    removeFromFields(draggedIndex);
    addToFields(draggedField, droppedIndex);
  }

  // Traverse the fields array as the ng-repeat renders it. Skip legacy fields.
  function __calcEffectiveDropIndex(droppedIndex) {
    var effectiveIndex = -1;
    for (var i = 0, length = vm.fields.length; i < length; i++) {
      if (!vm.fields[i].legacy)
        effectiveIndex++;
      if (effectiveIndex === droppedIndex && !vm.fields[i].legacy)
        return i;
    }
  }


  /*****************************************************************************************************************
   * Controller Actions
   ****************************************************************************************************************/

  function toggleFieldModes() {
    var keys = Object.keys(vm.fieldApis);
    var i = keys.length;
    while (i--) {
      vm.fieldApis[keys[i]].setMode('builder-view');
    }
  }

  function setFieldMode(field, mode, retry) {
    if (field.legacy) {
      return; // ignore legacy fields, they are hidden from the user
    }
    if (isUndefined(retry)) retry = 0;
    if (vm.fieldApis[field.id]) {
      vm.fieldApis[field.id].setMode(mode);
    } else if (retry < 5) {
      $timeout(setFieldMode.bind(null, field, mode, ++retry), 20);
    } else {
      console.warn('failed to autoset mode');
    }
  }


  /*****************************************************************************************************************
   * Presentation Logic
   ****************************************************************************************************************/

  function initModeToggleListener() {
    $scope.$on('form-fields:change-mode', function (event, fieldMode) {
      forEach(vm.fieldApis, function (api, id) {
        if (id === fieldMode.field.id) return;
        api.setMode('builder-view');
      });
    });
  }
}


// WEBPACK FOOTER //
// ./src/js/forms/directives/builder/FormBuilderDirective.js